import type { AttackType, DefenseType } from "@/types/hero.types"

/**
 * Damage type effectiveness table based on Warcraft 3 style system
 * Values are multipliers (1.0 = 100% damage)
 *
 * From the reference images:
 * - Pierce: 150% vs Unarmored, 200% vs Light, 75% vs Medium, 35% vs Heavy/Fortified
 * - Siege: 150% vs Unarmored, 50% vs Medium, 150% vs Fortified
 * - Magic: 125% vs Light, 75% vs Medium, 200% vs Heavy, 35% vs Hero
 * - Normal: 150% vs Medium, 70% vs Fortified
 * - All types deal only 5% to Divine
 */
const damageMultiplierTable: Record<AttackType, Record<DefenseType, number>> = {
  normal: {
    unarmored: 1.0,
    light: 1.0,
    medium: 1.5,
    heavy: 1.0,
    fortified: 0.7,
    hero: 1.0,
    divine: 0.05,
  },
  pierce: {
    unarmored: 1.5,
    light: 2.0,
    medium: 0.75,
    heavy: 0.35,
    fortified: 0.35,
    hero: 1.0,
    divine: 0.05,
  },
  siege: {
    unarmored: 1.5,
    light: 1.0,
    medium: 0.5,
    heavy: 1.0,
    fortified: 1.5,
    hero: 0.5,
    divine: 0.05,
  },
  hero: {
    unarmored: 1.0,
    light: 1.0,
    medium: 1.0,
    heavy: 1.0,
    fortified: 0.5,
    hero: 1.0,
    divine: 0.05,
  },
  chaos: {
    unarmored: 1.0,
    light: 1.0,
    medium: 1.0,
    heavy: 1.0,
    fortified: 1.0,
    hero: 1.0,
    divine: 1.0, // Chaos damages everything equally
  },
  magic: {
    unarmored: 1.0,
    light: 1.25,
    medium: 0.75,
    heavy: 2.0,
    fortified: 0.35,
    hero: 0.35,
    divine: 0.05,
  },
  spells: {
    unarmored: 1.0,
    light: 1.0,
    medium: 1.0,
    heavy: 1.0,
    fortified: 0.75,
    hero: 1.0,
    divine: 0.05,
  },
}

/**
 * Armor reduction table - how much damage is reduced per armor point
 * Based on the reference image showing armor values 0-19+
 */
const armorReductionTable: number[] = [
  0,    // 0 armor = 0% reduction
  0.06, // 1 armor = 6% reduction
  0.11, // 2 armor = 11% reduction
  0.15, // 3 armor = 15% reduction
  0.19, // 4 armor = 19% reduction
  0.23, // 5 armor = 23% reduction
  0.26, // 6 armor = 26% reduction
  0.29, // 7 armor = 29% reduction
  0.32, // 8 armor = 32% reduction
  0.35, // 9 armor = 35% reduction
  0.38, // 10 armor = 38% reduction
  0.40, // 11 armor = 40% reduction
  0.42, // 12 armor = 42% reduction
  0.44, // 13 armor = 44% reduction
  0.46, // 14 armor = 46% reduction
  0.48, // 15 armor = 48% reduction
  0.50, // 16 armor = 50% reduction
  0.52, // 17 armor = 52% reduction
  0.53, // 18 armor = 53% reduction
  0.55, // 19+ armor = 55% reduction (cap)
]

/**
 * Get armor damage reduction percentage (0 to 1)
 */
export const getArmorReduction = (armor: number): number => {
  const clampedArmor = Math.max(0, Math.min(19, Math.floor(armor)))
  return armorReductionTable[clampedArmor]
}

/**
 * Get damage type effectiveness multiplier
 */
export const getDamageMultiplier = (attackType: AttackType, defenseType: DefenseType): number => {
  return damageMultiplierTable[attackType]?.[defenseType] ?? 1.0
}

/**
 * Calculate final damage after type effectiveness and armor reduction
 */
export const calculateDamage = (
  baseDamage: number,
  attackType: AttackType,
  defenseType: DefenseType,
  targetArmor: number,
): number => {
  // Apply type effectiveness
  const typeMultiplier = getDamageMultiplier(attackType, defenseType)
  const damageAfterType = baseDamage * typeMultiplier

  // Apply armor reduction
  const armorReduction = getArmorReduction(targetArmor)
  const finalDamage = damageAfterType * (1 - armorReduction)

  return Math.max(1, Math.floor(finalDamage)) // Minimum 1 damage
}

/**
 * Get a human-readable description of the effectiveness
 */
export const getEffectivenessDescription = (multiplier: number): string => {
  if (multiplier >= 2.0) return "Very Effective"
  if (multiplier >= 1.5) return "Effective"
  if (multiplier >= 1.0) return "Normal"
  if (multiplier >= 0.5) return "Resistant"
  if (multiplier >= 0.35) return "Very Resistant"
  return "Immune"
}

/**
 * Get the color for effectiveness display
 */
export const getEffectivenessColor = (multiplier: number): string => {
  if (multiplier >= 2.0) return "#00ff00" // Bright green
  if (multiplier >= 1.5) return "#88ff88" // Light green
  if (multiplier >= 1.0) return "#ffffff" // White (neutral)
  if (multiplier >= 0.5) return "#ffaa00" // Orange
  if (multiplier >= 0.35) return "#ff6666" // Light red
  return "#ff0000" // Red
}
